import { MonitorKind } from '@signals/app-shell/services/intelPacketApi';

export interface MonitorFunction {
  name: string;
  detail: string;
}

export interface MonitorCatalogEntry {
  summary: string;
  collects: string;
  functions: MonitorFunction[];
  settings?: string;
}

export const MONITOR_CATALOG: Partial<Record<MonitorKind, MonitorCatalogEntry>> = {
  adtech: {
    summary:
      'Re-queries packet AdTech IDs and the packet geofence against the commercial location vendor.',
    collects:
      'Writes sightings and new-in-AOI devices onto the packet. Data typically lands 24–48 hours late and some areas have no coverage.',
    functions: [
      { name: 'Known-ID sighting', detail: 'Pings for AdTech IDs already on the packet, anywhere if the member is global, or only in the fence if scoped to AOI.' },
      { name: 'New device in AOI', detail: 'IDs seen inside the fence (plus buffer) that are not on the roster. Add one to keep watching it.' },
      { name: 'Coverage / lag wait', detail: 'Empty windows are recorded as no coverage or still waiting — not a collector failure.' },
    ],
    settings: 'Buffer expands the fence for the area query. 0 stays inside the fence.',
  },
  dfp: {
    summary:
      'Pulls warning and critical RF / signal alerts for packet devices, or for anything inside the fence if no device is listed.',
    collects:
      'Groups alerts by device and type. Info-level GNSS noise is ignored.',
    functions: [
      { name: 'GNSS interference', detail: 'Jamming or spoofing on packet devices or in the AOI.' },
      { name: 'Watchlist / unknown', detail: 'Watchlist hits, unknown or shadowed devices, drone detections.' },
      { name: 'RF environment', detail: 'MAC anomaly, BLE flood, IMSI-catcher suspect, rogue AP, evil twin.' },
    ],
    settings: 'Buffer expands the fence when the packet has no device members. 0 stays inside the fence.',
  },
  geofence: {
    summary:
      'Files enter / exit events from Pathfinder geofence activity for fences on this packet.',
    collects:
      'Each event becomes an observation, grouped by device and event type on the Activity tab.',
    functions: [
      { name: 'Enter / exit', detail: 'A watched device crossed a packet geofence.' },
      { name: 'AOI presence', detail: 'Same feed Area Search uses — not a linger, just a fence flip.' },
    ],
  },
  news: {
    summary:
      'Pulls DataHog / Overwatch media inside the packet geofence (plus buffer) and snapshots articles already attached to the packet.',
    collects:
      'Matches headlines, original and English body, and GDELT persons / orgs / themes.',
    functions: [
      { name: 'AOI media', detail: 'Articles geotagged in the fence that match the term filter (or all AOI reports if no terms).' },
      { name: 'Attached articles', detail: 'Overwatch items already on the roster are snapshotted into the case file.' },
      { name: 'AND / OR / NOT / phrase', detail: 'Optional term filter. Multi-word OR groups stay together (e.g. "Quiet Professionals").' },
    ],
    settings: 'Buffer expands the search area. Terms are optional — empty means every AOI report.',
  },
  pol: {
    summary:
      'Looks for repeating presence and for breaks from a subject’s usual pattern. System-generated — not an analyst conclusion.',
    collects:
      'Dense GPS uses Dwell Analysis stay-points. AdTech and watchlist IDs without dwells use return-to-place. Anomaly types below are produced by the Pattern of Life engine from that same history.',
    functions: [
      { name: 'Recurring presence', detail: 'Same stay-point or ~150 m cell on 2+ days. One ping is a visit, not a linger.' },
      { name: 'New in AOI', detail: 'GPS devices that dwelled in the fence but are not on the roster.' },
      { name: 'Historic / new AOIs', detail: 'Lookback on a member finds recurring places outside the packet fence.' },
      { name: 'Routine break', detail: 'Usually at a place by a given time, and they were not.' },
      { name: 'Unusual location', detail: 'A dwell somewhere they do not normally go.' },
      { name: 'Time shift', detail: 'Activity at hours that are off their baseline.' },
      { name: 'Dwell duration', detail: 'Stayed much longer or shorter than usual.' },
      { name: 'Route deviation', detail: 'Left a usual path.' },
      { name: 'Lapse break', detail: 'An unexpected gap in activity.' },
      { name: 'New relationship', detail: 'A new co-location or association.' },
      { name: 'ML daily score', detail: 'Isolation Forest (or the older autoencoder) flags a whole day as statistically unlike that entity’s history.' },
    ],
  },
};
